import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { appendOlderPage } from "@/lib/keyset-list";
import { apiFetch } from "@/services/http/api-fetch";

/** I-5: how many older pages a deep link may load looking for its announcement (50 each). */
export const DEEP_LINK_MAX_PAGES = 20;

type Announcement = {
  id: string;
  title: string;
  body: string;
  authorName: string | null;
  createdAt: string;
};

/**
 * COMM-3: a course's announcements for its students — newest first, full text. Opening the list
 * marks the reader's announcement notifications for this course read.
 * I-5 / R-13: `targetId` (a notification's `#a-<id>`) is scrolled into view, loading older pages
 * until it is on screen when it is not on the newest one.
 */
export function CourseAnnouncementsList({
  courseId,
  targetId,
}: {
  courseId: string;
  targetId?: string | null;
}) {
  const t = useTranslations("announcements");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [announcements, setAnnouncements] = useState<Announcement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // COMMS-12: the server sends the newest page; this is the cursor for the older ones.
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  // R-14: a failed "load more" is shown by its button; the list already on screen stays.
  const [moreError, setMoreError] = useState<string | null>(null);
  const deepLinkPages = useRef(0);
  const scrolledTo = useRef<string | null>(null);

  const errorText = useCallback(
    (err: unknown) =>
      apiErrorText(err, tRoot, "announcements.loadFailed", {
        namespace: "announcements.listErrors",
      }),
    [tRoot],
  );

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch(`/v1/courses/${courseId}/announcements`, {
          signal: controller.signal,
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as {
          data: { announcements: Announcement[]; nextCursor?: string | null };
        };
        setAnnouncements(json.data.announcements);
        setNextCursor(json.data.nextCursor ?? null);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setError(errorText(err));
        return;
      }
      // Best effort: the list is shown even if the unread markers cannot be cleared.
      await apiFetch(`/v1/courses/${courseId}/announcements/read`, { method: "POST" }).catch(
        () => undefined,
      );
    })();
    return () => controller.abort();
  }, [courseId, errorText]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const res = await apiFetch(
        `/v1/courses/${courseId}/announcements?before=${encodeURIComponent(nextCursor)}`,
      );
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data: { announcements: Announcement[]; nextCursor?: string | null };
      };
      setAnnouncements((current) => appendOlderPage(current ?? [], json.data.announcements));
      setNextCursor(json.data.nextCursor ?? null);
    } catch (err) {
      setMoreError(errorText(err));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [courseId, errorText, nextCursor]);

  // The announcement a notification links to (`#a-<id>`): scroll to it once it is on the page,
  // loading older pages (up to DEEP_LINK_MAX_PAGES) while it is not.
  const target =
    targetId !== undefined
      ? targetId
      : typeof window !== "undefined" && window.location.hash.startsWith("#a-")
        ? window.location.hash.slice(3)
        : null;
  useEffect(() => {
    if (!announcements || !target || scrolledTo.current === target) return;
    if (announcements.some((announcement) => announcement.id === target)) {
      scrolledTo.current = target;
      document.getElementById(`a-${target}`)?.scrollIntoView?.({ block: "start" });
      return;
    }
    if (!nextCursor || loadingMore || moreError) return;
    if (deepLinkPages.current >= DEEP_LINK_MAX_PAGES) return;
    deepLinkPages.current += 1;
    void loadMore();
  }, [announcements, target, nextCursor, loadingMore, moreError, loadMore]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-[var(--danger)]" data-testid="announcements-error">
        {error}
      </p>
    );
  }
  if (!announcements) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }
  if (announcements.length === 0) {
    return (
      <p className="text-sm text-[var(--text-muted)]" data-testid="announcements-empty">
        {t("empty")}
      </p>
    );
  }

  const formatWhen = (iso: string) =>
    new Date(iso).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <ul className="space-y-4" data-testid="announcements-list">
      {announcements.map((announcement) => (
        <li key={announcement.id}>
          <article
            id={`a-${announcement.id}`}
            className="scroll-mt-6 rounded-xl border border-[var(--border)] p-5"
            data-testid={`announcement-${announcement.id}`}
          >
            <h2 className="text-lg font-semibold text-[var(--text-heading)]">
              {announcement.title}
            </h2>
            <p className="mt-1 text-xs text-[var(--text-muted)]">
              {announcement.authorName
                ? t("postedBy", { name: announcement.authorName })
                : t("postedByTeam")}
              {" · "}
              <time dateTime={announcement.createdAt}>{formatWhen(announcement.createdAt)}</time>
            </p>
            <p
              className="mt-3 whitespace-pre-wrap text-sm text-[var(--text-secondary)]"
              data-testid={`announcement-body-${announcement.id}`}
            >
              {announcement.body}
            </p>
          </article>
        </li>
      ))}
      {nextCursor ? (
        <li>
          {moreError ? (
            <p
              role="alert"
              className="mb-2 text-sm text-[var(--danger)]"
              data-testid="announcements-more-error"
            >
              {moreError}
            </p>
          ) : null}
          <button
            type="button"
            className="text-sm underline disabled:opacity-50"
            disabled={loadingMore}
            onClick={() => void loadMore()}
            data-testid="announcements-more"
          >
            {t("loadMore")}
          </button>
        </li>
      ) : null}
    </ul>
  );
}
